<template lang="pug">
v-card(v-if="shares.length")
  v-card-title
    span Concentración de clientes
    ShowInfoBtn(@click="$refs.infoAlert.showAlert()")
  v-card-text
    InfoAlert(:message="alertMessage" ref="infoAlert")

    v-alert(dense text :color="severityColor" border="left" class="mb-4")
      span(v-html="message")

    .conc-row(v-for="c in shares" :key="c.name")
      .conc-head
        span.conc-name {{ c.name }}
        span.conc-pct {{ c.pct.toFixed(0) }}%
      .conc-track
        .conc-fill(:style="{ width: c.pct + '%', background: barColor(c) }")
</template>

<script>
import { mapActions } from 'vuex'
import InfoAlert from '../../common/InfoAlert.vue'
import ShowInfoBtn from '../../common/ShowInfoBtn.vue'

export default {
  props: {
    filters: {
      type: Object,
      required: true,
      default: () => ({})
    },
    projection: {
      type: Number,
      default: null,
    },
  },
  components: {
    InfoAlert,
    ShowInfoBtn,
  },
  data() {
    return {
      customers: [],
      alertMessage: `
<p>
Este indicador muestra <b>qué parte de tu margen depende de cada cliente</b>. Cuando uno solo concentra una porción muy grande, tu negocio queda expuesto: si ese cliente se va, se va con él buena parte de tu ganancia. Un reparto más equilibrado te da estabilidad y poder de negociación.
</p>`,
    }
  },
  computed: {
    total() {
      return this.customers.reduce((a, c) => a + c.value, 0)
    },
    shares() {
      if (!this.total) return []
      return [...this.customers]
        .filter(c => c.value > 0)
        .sort((a, b) => b.value - a.value)
        .map(c => ({ ...c, pct: (c.value / this.total) * 100 }))
    },
    top() {
      return this.shares[0] || null
    },
    top2pct() {
      return this.shares.slice(0, 2).reduce((a, c) => a + c.pct, 0)
    },
    severity() {
      if (!this.top) return 'low'
      if (this.top.pct >= 50) return 'high'
      if (this.top.pct >= 30) return 'med'
      return 'low'
    },
    severityColor() {
      return { high: 'error', med: 'warning', low: 'success' }[this.severity]
    },
    message() {
      if (!this.top) return ''
      if (this.severity === 'low') {
        return `Tu cartera está bien repartida: ningún cliente concentra más del 30% de tu margen. Buen balance de riesgo.`
      }
      const tone = this.severity === 'high' ? 'Riesgo alto de concentración' : 'Atención a la concentración'
      return `<b>${tone}:</b> ${this.pretty(this.top.name)} concentra el <b>${this.top.pct.toFixed(0)}%</b> de tu margen (tus 2 mayores clientes, el ${this.top2pct.toFixed(0)}%). Si te deja, perderías esa parte de tu ganancia — conviene diversificar tu cartera.`
    },
  },
  methods: {
    ...mapActions('commercial_projection', ['getOptimalMixByCustomerChart']),
    pretty(name) {
      if (!name) return ''
      return name.charAt(0) + name.slice(1).toLowerCase()
    },
    barColor(c) {
      if (c === this.top && this.severity === 'high') return '#E0533C'
      if (c === this.top && this.severity === 'med') return '#C9821B'
      return '#5575DF'
    },
    async fetchData() {
      const params = {
        ...this.filters,
        projection: this.projection || this.$route.params.id,
      }
      const { data, error } = await this.getOptimalMixByCustomerChart(params)
      if (!error && data && data.datasets) {
        this.customers = data.datasets.map(d => ({
          name: d.label,
          value: (d.data || []).reduce((a, v) => a + (v || 0), 0),
        }))
      }
    },
  },
  watch: {
    filters: {
      handler() { this.fetchData() },
      deep: true,
    },
  },
  created() {
    this.fetchData()
  },
}
</script>

<style scoped>
.conc-row { margin-bottom: 12px; }
.conc-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px; }
.conc-name { font-size: 13.5px; color: #1B2E38; }
.conc-pct { font-size: 13px; font-weight: 700; color: #4A5A66; font-variant-numeric: tabular-nums; }
.conc-track { height: 8px; border-radius: 6px; background: #F0F2F7; overflow: hidden; }
.conc-fill { height: 100%; border-radius: 6px; transition: width .3s ease; }
</style>
